import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useParticipantDetail, useMarkAttendance } from "../lib/queries";
import { Empty, ErrorBox, Loading } from "../components/Loading";
import { useToast } from "../components/Toast";
import { AddBalanceSheet } from "../components/AddBalanceSheet";
import { CoachPickerSheet } from "../components/CoachPickerSheet";
import { fmtDate } from "../lib/format";
import { setBackButton } from "../lib/tg";

interface ParticipantDetailData {
  participant: {
    id: string; name: string; birth_year: number | null;
    coaches: Array<{ id: string; name: string; is_primary: boolean }>;
  };
  parent: { name: string };
  balances: Array<{
    id: string; remaining: number; paid_amount: number | null; paid_at: string | null;
    note: string | null; created_at: string;
    plan: { name: string; sessions_count: number; activity_type: { name: string; emoji: string | null } };
  }>;
  bookings: Array<{
    id: string;
    status: "confirmed" | "attended" | "noshow" | "cancelled";
    needs_skipass: boolean;
    created_at: string;
    session: { id: string; starts_at: string; location_text: string | null; activity_type: { name: string; emoji: string | null } };
    attendance_log: Array<{ consumed_at: string; balance_id: string | null }>;
  }>;
}

interface TimelineItem {
  at: string;        // ISO date for sort
  date: Date;
  kind: "session" | "payment";
  text: string;
  sub?: string;
  status?: "attended" | "noshow" | "confirmed" | "cancelled";
  bookingId?: string;
  isPast?: boolean;
}

export default function ParticipantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, mode } = useAuth();
  const q = useParticipantDetail(id);
  const markAttendance = useMarkAttendance();
  const toast = useToast();
  const isTrainer = mode === "trainer" && !!user?.trainer_id;

  const backHref = isTrainer ? "/t/people" : "/me";
  useEffect(() => setBackButton(() => navigate(backHref)), [navigate, backHref]);

  const [addingKind, setAddingKind] = useState<"single" | "pack" | null>(null);
  const [pickingCoach, setPickingCoach] = useState(false);

  const data = q.data as ParticipantDetailData | undefined;

  const timeline = useMemo<TimelineItem[]>(() => {
    if (!data) return [];
    const items: TimelineItem[] = [];
    for (const b of data.balances) {
      items.push({
        at: b.paid_at ?? b.created_at,
        date: new Date(b.paid_at ?? b.created_at),
        kind: "payment",
        text: `💰 ${b.plan.activity_type.emoji ?? ""} ${b.note ?? b.plan.name}`,
        sub: b.paid_amount != null ? `${b.paid_amount.toFixed(0)}₴ · ${b.plan.sessions_count} зан.` : `${b.plan.sessions_count} зан.`,
      });
    }
    const now = Date.now();
    for (const b of data.bookings) {
      items.push({
        at: b.session.starts_at,
        date: new Date(b.session.starts_at),
        kind: "session",
        text: `${b.session.activity_type.emoji ?? ""} ${b.session.activity_type.name}${b.needs_skipass ? " 🎫" : ""}`,
        sub: b.session.location_text ?? undefined,
        status: b.status,
        bookingId: b.id,
        isPast: new Date(b.session.starts_at).getTime() < now,
      });
    }
    items.sort((a, b) => b.at.localeCompare(a.at));
    return items;
  }, [data]);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox message={(q.error as Error).message} />;
  if (!data) return <ErrorBox message="Не знайдено" />;

  const { participant, parent, balances, bookings } = data;
  const stats = computeStats(bookings);

  return (
    <div className="p-4 space-y-4">
      <header>
        <h1 className="text-2xl font-bold">{participant.name}</h1>
        <div className="text-tg-hint text-sm mt-1">
          {participant.birth_year && <span>{participant.birth_year} р.н. · </span>}
          {isTrainer && <span>Батько: {parent.name}</span>}
        </div>
        <div className="text-sm mt-2">
          {participant.coaches.length === 0 ? (
            !isTrainer ? (
              <button className="text-tg-link" onClick={() => setPickingCoach(true)}>
                + Обрати тренера
              </button>
            ) : (
              <span className="text-tg-hint">Без тренера</span>
            )
          ) : (
            <span>
              <span className="text-tg-hint">Тренер{participant.coaches.length > 1 ? "и" : ""}: </span>
              {participant.coaches.map((c) => c.name).join(", ")}
              {!isTrainer && (
                <button className="text-tg-link ml-2" onClick={() => setPickingCoach(true)}>
                  змінити
                </button>
              )}
            </span>
          )}
        </div>
      </header>

      <section className="card">
        <h2 className="font-semibold mb-2">Баланс</h2>
        {balances.filter((b) => b.remaining !== 0).length === 0 ? (
          <div className="text-tg-hint text-sm">Активних абонементів немає.</div>
        ) : (
          <ul className="space-y-1 text-sm">
            {balances.filter((b) => b.remaining !== 0).map((b) => {
              const debt = b.remaining < 0;
              return (
                <li key={b.id} className="flex justify-between">
                  <span>{b.plan.activity_type.emoji ?? ""} {b.note ?? b.plan.name}</span>
                  <span className={`font-medium tabular-nums ${debt ? "text-amber-700" : ""}`}>
                    {debt ? `${b.remaining} борг` : `${b.remaining}/${b.plan.sessions_count}`}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {isTrainer && (
          <div className="flex gap-2 mt-3">
            <button
              className="btn-secondary text-sm flex-1"
              onClick={() => setAddingKind("single")}
            >+ Разове</button>
            <button
              className="btn-secondary text-sm flex-1"
              onClick={() => setAddingKind("pack")}
            >+ Абонемент</button>
          </div>
        )}
      </section>

      <section className="card">
        <h2 className="font-semibold mb-2">Статистика</h2>
        <div className="grid grid-cols-3 gap-2 text-center text-sm">
          <div>
            <div className="text-2xl font-bold text-green-700">{stats.attended}</div>
            <div className="text-tg-hint text-xs">відвідано</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-amber-600">{stats.noshow}</div>
            <div className="text-tg-hint text-xs">пропущено</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-tg-link">{stats.booked}</div>
            <div className="text-tg-hint text-xs">
              записано
              {stats.upcoming > 0 && <div className="text-[10px]">{stats.upcoming} майбутніх</div>}
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-semibold mb-2 px-1">Історія</h2>
        {timeline.length === 0 ? (
          <Empty label="Поки немає записів." />
        ) : (
          <ul className="space-y-2">
            {timeline.map((item, i) => {
              const showMarkButtons =
                item.kind === "session" &&
                item.isPast &&
                item.status === "confirmed" &&
                !!item.bookingId;
              return (
                <li key={i} className={`card ${item.kind === "payment" ? "bg-green-500/5" : ""}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">{item.text}</div>
                      {item.sub && <div className="text-tg-hint text-xs truncate">{item.sub}</div>}
                    </div>
                    <div className="text-right">
                      <div className="text-sm tabular-nums">{fmtDate(item.date)}</div>
                      {item.status && <div className={`text-xs ${statusClass(item.status)}`}>{statusLabel(item.status)}</div>}
                    </div>
                  </div>
                  {showMarkButtons && (
                    <div className="flex gap-2 mt-2">
                      <button
                        className="chip text-xs flex-1 justify-center bg-green-500/10 text-green-700"
                        onClick={() => markAttendance.mutate(
                          { booking_id: item.bookingId!, status: "attended" },
                          { onError: (e) => toast.error((e as Error).message) },
                        )}
                      >✓ Був</button>
                      <button
                        className="chip text-xs flex-1 justify-center bg-amber-500/10 text-amber-700"
                        onClick={() => markAttendance.mutate(
                          { booking_id: item.bookingId!, status: "noshow" },
                          { onError: (e) => toast.error((e as Error).message) },
                        )}
                      >✗ Не був</button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {addingKind && (
        <AddBalanceSheet
          participant={{ id: participant.id, name: participant.name }}
          kind={addingKind}
          onClose={() => setAddingKind(null)}
        />
      )}

      {pickingCoach && (
        <CoachPickerSheet
          participant={{
            id: participant.id,
            name: participant.name,
            coach_ids: participant.coaches.map((c) => c.id),
          }}
          onClose={() => setPickingCoach(false)}
        />
      )}
    </div>
  );
}

function computeStats(bookings: ParticipantDetailData["bookings"]): { attended: number; noshow: number; booked: number; upcoming: number } {
  let attended = 0, noshow = 0, booked = 0, upcoming = 0;
  const now = Date.now();
  for (const b of bookings) {
    if (b.status === "attended") attended++;
    else if (b.status === "noshow") noshow++;
    else if (b.status === "confirmed") {
      booked++;
      if (new Date(b.session.starts_at).getTime() > now) upcoming++;
    }
  }
  return { attended, noshow, booked, upcoming };
}

function statusLabel(s: "attended" | "noshow" | "confirmed" | "cancelled"): string {
  return ({ attended: "✓ був", noshow: "✗ не прийшов", confirmed: "запис", cancelled: "скасовано" })[s];
}
function statusClass(s: "attended" | "noshow" | "confirmed" | "cancelled"): string {
  return ({
    attended: "text-green-700",
    noshow: "text-amber-600",
    confirmed: "text-tg-link",
    cancelled: "text-tg-hint line-through",
  })[s];
}
